import { Chip, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useScopeLabel } from '../people';
import { peopleWhoCanLogTime } from '../sampleData';
import type { SettingsSample } from '../sampleData';
import type { DetailsValues } from '../state';
import { scopeNote } from '../state/detailsView';

import { DetailsCard, ValueNote } from './DetailsCard';
import type { CardEditProps } from './DetailsCard';

import { MaterialSymbol } from '@/components/MaterialSymbol';

/**
 * Who can log time (brief §3): the level, how many people it reaches as a
 * count chip, and its note — "· default" or "· changed from default", and,
 * since every project is Draft (decision 2), "· starts when the project goes
 * live". Under it, whether people are asked for hours left (ETC).
 *
 * The levels and the count come from `sampleData/reach.ts` (v1's, kept), so the
 * numbers are computed from the sample, never typed in.
 */
export type LogTimeCardProps = {
  values: Pick<DetailsValues, 'logTimeScope' | 'askForEtc' | 'legalEntityId' | 'departmentId'>;
  defaults: Pick<DetailsValues, 'logTimeScope'>;
  sample: SettingsSample;
  /** Edit mode, for a card that has its form. */
  edit?: CardEditProps;
};

export const LogTimeCard = ({ values, defaults, sample, edit }: LogTimeCardProps) => {
  const { t } = useTranslation('pmDetails');
  const scopeLabel = useScopeLabel(sample);
  const setting = {
    scope: values.logTimeScope,
    legalEntityId: values.legalEntityId,
    departmentId: values.departmentId,
  };
  const count = peopleWhoCanLogTime(sample, setting).size;
  const note = scopeNote(values, defaults);
  return (
    <DetailsCard
      icon="schedule"
      title={t('logTime.Title')}
      automationId="details-card-log-time"
      {...edit}
    >
      <Stack sx={{ gap: 1.5 }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography
            variant="bodyRegular"
            component="span"
            data-automation-id="details-log-time-level"
          >
            {scopeLabel(setting)}
          </Typography>
          <Chip
            size="small"
            color="fog"
            monoText
            label={t('logTime.People', { count })}
            data-automation-id="details-log-time-count"
          />
          <ValueNote automationId="details-log-time-note">
            · {t(`logTime.note.${note}`)} · {t('logTime.note.startsLive')}
          </ValueNote>
        </Stack>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', gap: 0.75 }}
          data-automation-id="details-log-time-etc"
        >
          <Typography variant="bodyRegular" component="span">
            {values.askForEtc ? t('logTime.EtcAsked') : t('logTime.EtcNotAsked')}
          </Typography>
          {values.askForEtc ? (
            <MaterialSymbol name="check" size={18} color="var(--color-success-80)" />
          ) : null}
        </Stack>
      </Stack>
    </DetailsCard>
  );
};
